
import { supabase } from "@/integrations/supabase/client";
import { WebNovel } from "../types";
import { toast } from "sonner";

export async function fetchWebNovel(id: string): Promise<WebNovel> {
  try {
    console.log(`Fetching webnovel with ID: ${id}`);
    
    // First fetch the webnovel without the join
    const { data: webnovelData, error: webnovelError } = await supabase
      .from("webnovels")
      .select("*")
      .eq("id", id)
      .single();
    
    if (webnovelError) {
      console.error("Error fetching webnovel:", webnovelError);
      throw new Error(`Erro ao buscar webnovel: ${webnovelError.message}`);
    }
    
    if (!webnovelData) {
      throw new Error("Webnovel não encontrada");
    }
    
    console.log("Webnovel data:", webnovelData);
    
    // Increment view count
    try {
      await supabase.rpc("increment_content_view", {
        content_id: id,
        content_type: "webnovel"
      });
      console.log("View count incremented successfully");
    } catch (viewError) {
      console.error("Error incrementing view count:", viewError);
      // Don't fail the entire request if view counting fails
    }
    
    // Count the number of chapters
    const { count: chaptersCount, error: chaptersError } = await supabase
      .from("chapters")
      .select("*", { count: 'exact', head: true })
      .eq("webnovel_id", id);
    
    if (chaptersError) {
      console.error("Error counting chapters:", chaptersError);
    }
    
    // Fetch all chapters to display in the sidebar
    const { data: chaptersData, error: allChaptersError } = await supabase
      .from("chapters")
      .select("*")
      .eq("webnovel_id", id)
      .order("chapter_number", { ascending: true });
      
    if (allChaptersError) {
      console.error("Error fetching chapters:", allChaptersError);
    }
    
    // If there's an author_id, fetch the author's profile separately
    let author = null;
    if (webnovelData.author_id) {
      try {
        const { data: authorData, error: authorError } = await supabase
          .from("profiles")
          .select("id, username, avatar_url")
          .eq("id", webnovelData.author_id)
          .maybeSingle(); // Using maybeSingle instead of single to prevent errors
        
        if (!authorError && authorData) {
          author = {
            id: authorData.id || "",
            username: authorData.username || "Usuário",
            avatar_url: authorData.avatar_url
          };
          console.log("Author data:", author);
        } else {
          console.error("Error fetching author:", authorError);
        }
      } catch (authorFetchError) {
        console.error("Exception fetching author:", authorFetchError);
      }
    }
    
    // Get view count for this novel
    const { data: viewCountData, error: viewCountError } = await supabase
      .from("webnovel_analytics")
      .select("views")
      .eq("webnovel_id", id)
      .single();
      
    let views = 0;
    if (!viewCountError && viewCountData) {
      views = viewCountData.views || 0;
    }
    
    const novel: WebNovel = {
      id: webnovelData.id,
      title: webnovelData.title,
      description: webnovelData.description || "",
      cover_url: webnovelData.cover_url,
      category: webnovelData.category || "",
      age_rating: webnovelData.age_rating || "Livre",
      language: webnovelData.language || "Português",
      created_at: webnovelData.created_at,
      updated_at: webnovelData.updated_at,
      author_id: webnovelData.author_id,
      author: author,
      views: views,
      archived: webnovelData.archived || false,
      chapters: chaptersData || []
    };
    
    return novel;
  } catch (error: any) {
    console.error("Error in fetchWebNovel:", error);
    toast.error(error.message || "Erro ao carregar webnovel");
    throw error;
  }
}

export default fetchWebNovel;
